import React from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Cabinet } from 'lib/modelBuilder/entities/Cabinet';
import { createStructuredSelector } from 'reselect';
import { RootState } from 'containers/PanesPanel/types';
import { makeSelectLastHistoryCabinet } from 'containers/Configurator/selectors/history';
import { restoreCabinet } from 'containers/Configurator/actions/furniture';
import slatImg from 'assets/images/handles/slat.png';
import ecoImg from 'assets/images/handles/eco.png';
import ecoPlusImg from 'assets/images/handles/ecoPlus.png';
import BigOptionButton from 'components/BigOptionButton';
import { useTranslation } from 'react-i18next';
import Picker from 'components/Picker';
import { Color } from 'lib/modelBuilder/entities/Color';
import {
  selectPaintDrawerHandleAction,
  updateHandleDrawerAction,
} from 'containers/HandlesPanel/actions';
import ItemPicker from 'helpers/ItemPicker';
import {
  DoorHandleType,
  DrawerHandleType,
  ECO_COLOR,
  ECO_PLUS_COLOR,
} from 'lib/modelBuilder/constants';
import { setColorsPanelAction } from 'containers/Configurator/actions/general';
import { makeSelectEcoColors } from '../../DrawersPanel/selectors';
import { updateDrawersAction } from '../../DrawersPanel/actions';
import { findColor } from '../../../lib/modelBuilder/utils/colorHelper';
import { ConfiguratorMode } from '../../ModeBar/reducer';
import { makeSelectConfiguratorMode } from '../../ModeBar/selectors';
import { makeSelectPaintTypeHandles } from '../selectors';

interface StateProps {
  ecoColors: ItemPicker[];
  lastHistoryCabinet: Cabinet;
  mode: ConfiguratorMode;
  paintType: DrawerHandleType | DoorHandleType | undefined;
}

const stateSelector = createStructuredSelector<RootState, StateProps>({
  ecoColors: makeSelectEcoColors(),
  lastHistoryCabinet: makeSelectLastHistoryCabinet(),
  mode: makeSelectConfiguratorMode(),
  paintType: makeSelectPaintTypeHandles(),
});

const OthersHandlePicker = () => {
  const dispatch = useDispatch();
  const { lastHistoryCabinet, mode, paintType } = useSelector(stateSelector);
  const { t } = useTranslation();

  const onLeave = () => {
    // Restore the last history element
    dispatch(restoreCabinet(lastHistoryCabinet));
  };

  const updateDrawerHandle = (color: Color, preview: boolean) => {
    if (mode === ConfiguratorMode.handles) {
      dispatch(selectPaintDrawerHandleAction(DrawerHandleType.Slat));
    } else {
      dispatch(updateHandleDrawerAction(DrawerHandleType.Slat, color, preview));
    }
  };

  const onClickItem = (item: ItemPicker) => {
    updateDrawerHandle(item.color as Color, false);
  };

  const onMouseOverItem = (item: ItemPicker) => {
    if (mode === ConfiguratorMode.selector)
      updateDrawerHandle(item.color as Color, true);
  };

  return (
    <Picker title={t('Others')}>
      <BigOptionButton
        imgSrc={slatImg}
        title={t('Slat')}
        handleClick={onClickItem}
        handleDoubleClick={() => {
          if (mode === ConfiguratorMode.selector)
            dispatch(setColorsPanelAction());
        }}
        handleHover={onMouseOverItem}
        onLeave={onLeave}
        selected={
          mode === ConfiguratorMode.handles &&
          paintType === DrawerHandleType.Slat
        }
      />
      <BigOptionButton
        imgSrc={ecoImg}
        title={t('Eco')}
        handleClick={() => {
          if (mode === ConfiguratorMode.selector) {
            dispatch(updateDrawersAction(findColor(ECO_COLOR), false));
          } else {
            dispatch(
              selectPaintDrawerHandleAction(
                DrawerHandleType.Eco,
                findColor(ECO_COLOR),
              ),
            );
          }
        }}
        handleDoubleClick={() => {
          if (mode === ConfiguratorMode.selector)
            dispatch(setColorsPanelAction());
        }}
        handleHover={() => {
          if (mode === ConfiguratorMode.selector)
            dispatch(updateDrawersAction(findColor(ECO_COLOR), true));
        }}
        onLeave={onLeave}
        selected={
          mode === ConfiguratorMode.handles &&
          paintType === DrawerHandleType.Eco
        }
      />
      <BigOptionButton
        imgSrc={ecoPlusImg}
        title={t('Eco+')}
        handleClick={() => {
          if (mode === ConfiguratorMode.selector) {
            dispatch(updateDrawersAction(findColor(ECO_PLUS_COLOR), false));
          } else {
            dispatch(
              selectPaintDrawerHandleAction(
                DrawerHandleType.EcoPlus,
                findColor(ECO_PLUS_COLOR),
              ),
            );
          }
        }}
        handleDoubleClick={() => {
          if (mode === ConfiguratorMode.selector)
            dispatch(setColorsPanelAction());
        }}
        handleHover={() => {
          if (mode === ConfiguratorMode.selector)
            dispatch(updateDrawersAction(findColor(ECO_PLUS_COLOR), true));
        }}
        onLeave={onLeave}
        selected={
          mode === ConfiguratorMode.handles &&
          paintType === DrawerHandleType.EcoPlus
        }
      />
    </Picker>
  );
};

export default OthersHandlePicker;
